<template>
    <div
        class="notice-bar"
        v-if="visibleItems.length"
        :style="noticeBarStyle"
        @mouseenter="handleMouseEnter"
        @mouseleave="handleMouseLeave"
    >
        <transition-group
            name="notice-fade"
            tag="div"
            class="notice-bar__inner"
            :class="{
                'notice-bar__inner--split': visibleItems.length === 2,
                'notice-bar__inner--single': visibleItems.length === 1,
            }"
        >
            <span
                v-for="(item, index) in visibleItems"
                :key="`${item.id || item._id || item.title || 'notice'}-${rotationKey}-${index}`"
                class="notice-bar__item"
                :class="{
                    'notice-bar__item--left': visibleItems.length === 2 && index === 0,
                    'notice-bar__item--right': visibleItems.length === 2 && index === 1,
                    'notice-bar__item--clickable': item.showInPopup,
                }"
                @click="handleItemClick(item)"
            >
                <span class="notice-bar__text">
                    <template v-if="item.parsed?.divCount === 1 && item.parsed?.href">
                        <a
                            class="notice-bar__full-link"
                            :href="item.parsed.href"
                            :style="{ color: item.parsed?.color || '#fff' }"
                            rel="noopener noreferrer"
                            @click.stop
                        >
                            {{ item.parsed?.text || item?.title || item?.fields?.title || item?.fields?.text || item?.text || '' }}
                        </a>
                    </template>
                    <template v-else>
                        {{ item.parsed?.text || item?.title || item?.fields?.title || item?.fields?.text || item?.text || '' }}
                    </template>
                </span>
                <a
                    v-if="item.parsed?.divCount !== 1 && item.parsed?.href && !item.showInPopup"
                    class="notice-bar__link"
                    :href="item.parsed.href"
                    :style="{ color: item.parsed?.color || '#fff' }"
                    rel="noopener noreferrer"
                    @click.stop
                >
                    {{ 'details->' }}
                </a>
                <span
                    v-if="item.showInPopup"
                    class="notice-bar__popup-indicator"
                    :style="{ color: item.parsed?.color || '#fff' }"
                >
                    {{  }}
                </span>
            </span>
        </transition-group>

        <!-- Image Popup Modal -->
        <Teleport to="body">
            <div v-if="showPopup" class="notice-popup-overlay" @click="closePopup">
                <div class="notice-popup-content" @click.stop>
                    <button class="notice-popup-close" @click="closePopup">&times;</button>
                    <img :src="popupImageUrl" alt="Notice Image" class="notice-popup-image" />
                </div>
            </div>
        </Teleport>
    </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import HttpClient from "@/assets/apis/http-commons/HttpClient";
import { host, apiKey } from "@/assets/apis/config";

const noticeItems = ref([]);
const currentIndex = ref(0);
const paused = ref(false);
const showPopup = ref(false);
const popupImageUrl = ref("");
const ROTATION_MS = 5000;
let rotationTimer = null;

const handleItemClick = (item) => {
    if (item.showInPopup && item.parsed?.imageUrl) {
        popupImageUrl.value = item.parsed.imageUrl;
        showPopup.value = true;
    }
};

const closePopup = () => {
    showPopup.value = false;
    popupImageUrl.value = "";
};

const noticeBarStyle = computed(() => {
    const background = noticeItems.value?.[0]?.parsed?.background;
    return background ? { background } : { background: "#111" };
});

const visibleItems = computed(() => {
    const list = noticeItems.value;
    if (!list?.length) return [];
    if (list.length <= 2) return list;

    const first = list[currentIndex.value % list.length];
    const second = list[(currentIndex.value + 1) % list.length];
    return [first, second];
});

const rotationKey = computed(() => `${currentIndex.value}-${visibleItems.value.length}`);

const parseNoticeContent = (content, showInPopup) => {
    if (!content || typeof content !== "string") {
        return { text: "", href: "", background: "", color: "", imageUrl: "" };
    }

    let imageUrl = "";

    if (process.client && typeof window !== "undefined" && window.DOMParser) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(content, "text/html");
        const wrapper = doc.body?.firstElementChild || doc.body;
        const link = wrapper?.querySelector("a") || doc.querySelector("a");
        const textWrapper = wrapper ? wrapper.cloneNode(true) : null;
        textWrapper?.querySelector("a")?.remove();
        const divCount = doc.querySelectorAll("div").length || 0;
        
        // Extract image URL if showInPopup is true
        if (showInPopup && link) {
            const href = link?.getAttribute("href") || "";
            if (href && (href.match(/\.(jpg|jpeg|png|gif|webp|svg)$/i) || href.match(/cdn\./i))) {
                imageUrl = href;
            }
        }
        
        return {
            text: textWrapper?.textContent?.trim() || wrapper?.textContent?.trim() || "",
            href: link?.getAttribute("href") || "",
            background: wrapper?.style?.background || "",
            color: link?.style?.color || "",
            linkText: link?.textContent?.trim() || "Click here",
            divCount: divCount || 0,
            imageUrl: imageUrl,
        };
    }

    const backgroundMatch = content.match(/background\s*:\s*([^;\"]+)/i);
    const hrefMatch = content.match(/href\s*=\s*"([^"]+)"/i);
    const textMatch = content.match(/>([^<]+)</);
    const colorMatch = content.match(/color\s*:\s*([^;\"]+)/i);
    const linkTextMatch = content.match(/<a[^>]*>([^<]+)<\/a>/i);
    const divCount = (content.match(/<div\b/gi) || []).length;

    // Extract image URL if showInPopup is true
    if (showInPopup && hrefMatch) {
        const href = hrefMatch[1];
        if (href && (href.match(/\.(jpg|jpeg|png|gif|webp|svg)$/i) || href.match(/cdn\./i))) {
            imageUrl = href;
        }
    }

    return {
        text: textMatch?.[1]?.trim() || "",
        href: hrefMatch?.[1] || "",
        background: backgroundMatch?.[1]?.trim() || "",
        color: colorMatch?.[1]?.trim() || "",
        linkText: linkTextMatch?.[1]?.trim() || "Click here",
        divCount: divCount || 0,
        imageUrl: imageUrl,
    };
};

const stopRotation = () => {
    if (rotationTimer) {
        clearInterval(rotationTimer);
        rotationTimer = null;
    }
};

const startRotation = () => {
    stopRotation();
    if (noticeItems.value.length > 2) {
        rotationTimer = setInterval(() => {
            if (paused.value) return;
            currentIndex.value = (currentIndex.value + 2) % noticeItems.value.length;
        }, ROTATION_MS);
    }
};

const handleMouseEnter = () => {
    paused.value = true;
};

const handleMouseLeave = () => {
    paused.value = false;
};

const fetchNoticeBars = async () => {
    const client = new HttpClient();
    const url = `${host}/api/notice/bars/search`;
    const headers = {
        "Content-Type": "application/json",
        "X-API-KEY": apiKey,
        "X-DEVICE-ID": "aa=",
    };

    if (process.client) {
        const token = localStorage.getItem("token");
        if (token && token.length > 0) headers["X-AUTH-TOKEN"] = token;
    }

    const params = {
        textParams: [
        
            {
                field: "status",
                value: "ACTIVE",
                matchType: "EXACT",
            },
        ],
        page: 1,
        size: 10
        // status: "active",
    };

    const res = await client.makeRequest(url, "POST", params, headers);
    if (res?.status === 200 && !res?.networkError && res?.body?.list) {
        noticeItems.value = res.body.list.map((item) => ({
            ...item,
            parsed: parseNoticeContent(item?.content || "", item?.showInPopup || false),
        }));
        currentIndex.value = 0;
        startRotation();
    }
};

onMounted(async () => {
    await fetchNoticeBars();
});

watch(
    () => noticeItems.value.length,
    (len) => {
        currentIndex.value = 0;
        if (len > 2) {
            startRotation();
        } else {
            stopRotation();
        }
    }
);

onBeforeUnmount(() => {
    stopRotation();
});
</script>

<style scoped>
.notice-bar {
    background: #111;
    color: #fff;
    padding: 6px 0;
    overflow: hidden;
    position: relative;
}

.notice-bar__inner {
    display: flex;
    gap: 24px;
    padding: 0 24px;
    white-space: nowrap;
    justify-content: center;
    position: relative;
    align-items: center;
    min-height: 30px;
}

.notice-bar__inner--split {
    justify-content: space-between;
}

.notice-bar__inner--split::after {
    content: "";
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    width: 1px;
    height: 16px;
    background: rgba(255, 255, 255, 0.5);
}

.notice-bar__inner--single {
    justify-content: center;
}

.notice-bar__inner--single .notice-bar__item {
    flex: 0 0 auto;
    justify-content: center;
}

.notice-bar__item {
    font-size: 13px;
    letter-spacing: 0.2px;
    display: flex;
    align-items: center;
    gap: 6px;
    flex: 1 1 0;
    min-width: 0;
    text-transform: uppercase;
    font-weight: 700;
}

.notice-bar__item--left {
    flex: 1 1 0;
    justify-content: flex-end;
    padding-right: 18px;
}

.notice-bar__item--right {
    flex: 1 1 0;
    justify-content: flex-start;
    padding-left: 18px;
}

.notice-bar__link {
    text-decoration: none;
    font-size: 10px;
    border-bottom: 1px solid transparent;
    transition: border-color 0.2s ease, opacity 0.2s ease;
    text-transform: none;
    font-weight: 400;
    margin-top: 0.3rem;
}

.notice-bar__full-link {
    color: inherit;
    text-decoration: none;
}

.notice-bar__full-link:hover {
    text-decoration: underline;
}

.notice-bar__link:hover {
    border-color: currentColor;
    opacity: 0.85;
}

.notice-fade-enter-active,
.notice-fade-leave-active {
    transition: opacity 0.45s cubic-bezier(0.4, 0.0, 0.2, 1), transform 0.45s cubic-bezier(0.4, 0.0, 0.2, 1);
}

.notice-fade-enter-from,
.notice-fade-leave-to {
    opacity: 0;
    transform: translateY(10px);
}

.notice-fade-leave-active {
    position: absolute;
    left: 24px;
    right: 24px;
}

.notice-bar__item--clickable {
    cursor: pointer;
}

.notice-bar__popup-indicator {
    font-size: 10px;
    opacity: 0.8;
}

.notice-popup-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.8);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 9999;
}

.notice-popup-content {
    position: relative;
    max-width: 90%;
    max-height: 90%;
}

.notice-popup-close {
    position: absolute;
    top: -40px;
    right: 0;
    background: none;
    border: none;
    color: #fff;
    font-size: 32px;
    cursor: pointer;
    line-height: 1;
}

.notice-popup-image {
    max-width: 100%;
    max-height: 85vh;
    object-fit: contain;
    border-radius: 4px;
}
</style>
